import React, { useMemo, useState } from 'react';
import { TrendingUp, Wallet, Percent, Activity } from 'lucide-react';
import { PillTabBar } from '../../../reusable';
import { money } from '../treasury/treasuryUi';
import BudgetVsActualChart from './BudgetVsActualChart';
import BudgetStatementTable from './BudgetStatementTable';
import BudgetSalesOverview from './BudgetSalesOverview';
import BudgetCashFlowOverview from './BudgetCashFlowOverview';
import BudgetRawMaterialsOverview from './BudgetRawMaterialsOverview';

const OVERVIEW_TABS = [
  { key: 'pnl', label: 'Estado de Resultados' },
  { key: 'cashflow', label: 'Flujo de Caja' },
  { key: 'sales', label: 'Ventas' },
  { key: 'materials', label: 'Materias Primas' },
];

const lastSeriesMonth = (values = []) => {
  let last = 0;
  (values || []).forEach((value, index) => {
    if (Math.abs(Number(value) || 0) > 0.004) last = index + 1;
  });
  return last;
};

const lastStatementMonth = (nodes = []) => {
  let last = 0;
  const walk = (list) => {
    (list || []).forEach((node) => {
      (node.months || []).forEach((value, index) => {
        if (Math.abs(Number(value) || 0) > 0.004) last = Math.max(last, index + 1);
      });
      (node.actual_months || []).forEach((value, index) => {
        if (Math.abs(Number(value) || 0) > 0.004) last = Math.max(last, index + 1);
      });
      walk(node.children);
    });
  };
  walk(nodes);
  return last;
};

const CARD_TONES = {
  dark: {
    light: { bg: '#0f172a', color: '#f8fafc', muted: '#94a3b8' },
    dark: { bg: '#020617', color: '#f8fafc', muted: '#94a3b8' },
  },
  mint: {
    light: { bg: '#d1fae5', color: '#064e3b', muted: '#047857' },
    dark: { bg: '#064e3b', color: '#d1fae5', muted: '#6ee7b7' },
  },
  rose: {
    light: { bg: '#fce7f3', color: '#9d174d', muted: '#be185d' },
    dark: { bg: '#831843', color: '#fce7f3', muted: '#f9a8d4' },
  },
  sky: {
    light: { bg: '#e0f2fe', color: '#0c4a6e', muted: '#0369a1' },
    dark: { bg: '#0c4a6e', color: '#e0f2fe', muted: '#7dd3fc' },
  },
};

const formatCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toLocaleString('es-CL', { maximumFractionDigits: 1 })} mil MM`;
  if (abs >= 1e6) return `$${(n / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })} MM`;
  if (abs >= 1e5) return `$${(n / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })} mil`;
  return money(n);
};

const formatPercent = (value) => {
  if (value == null || value === '' || Number.isNaN(Number(value))) return null;
  return `${Number(value).toLocaleString('es-CL', { maximumFractionDigits: 1 })}%`;
};

const formatGallons = (value) => {
  const n = Number(value || 0);
  return `${n.toLocaleString('es-CL', { maximumFractionDigits: 1 })} ${Math.abs(n) === 1 ? 'galón' : 'galones'}`;
};

const totalSalesGallons = (salesItems = []) =>
  (salesItems || []).reduce((sum, item) => {
    const months = item.quantity_months || [];
    if (months.length) {
      return sum + months.reduce((acc, value) => acc + Number(value || 0), 0);
    }
    return sum + Number(item.quantity || 0);
  }, 0);

const nodeByKey = (nodes = [], key) => (nodes || []).find((row) => row.key === key) || null;

const MetricCard = ({ toneKey, isDarkMode, icon: Icon, label, value, hint }) => {
  const tone = CARD_TONES[toneKey][isDarkMode ? 'dark' : 'light'];
  return (
    <div
      style={{
        background: tone.bg,
        color: tone.color,
        borderRadius: 18,
        padding: '16px 18px',
        minWidth: 0,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: tone.muted }}>
          {label}
        </span>
        {Icon ? <Icon size={16} color={tone.muted} /> : null}
      </div>
      <div style={{ marginTop: 10, fontSize: 26, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em' }}>{value}</div>
      {hint ? <div style={{ marginTop: 8, fontSize: 11, color: tone.muted }}>{hint}</div> : null}
    </div>
  );
};

const BudgetOverview = ({
  budget = null,
  periodTitle = '',
  isDarkMode = false,
  ui,
  loading = false,
  canEdit = false,
  onChanged,
}) => {
  const [overviewTab, setOverviewTab] = useState('pnl');
  const monthAxis = useMemo(
    () => (Array.isArray(budget?.month_axis) ? budget.month_axis : []),
    [budget]
  );
  const monthCount = useMemo(() => {
    if (monthAxis.length) return monthAxis.length;
    const last = Math.max(
      lastSeriesMonth(budget?.monthly_budget),
      lastSeriesMonth(budget?.monthly_actual),
      lastStatementMonth(budget?.statement),
      Number(budget?.month_count || 0)
    );
    return Math.max(1, last);
  }, [budget, monthAxis]);
  const resultNode = useMemo(() => nodeByKey(budget?.statement, 'resultado'), [budget]);
  const incomeNode = useMemo(
    () => nodeByKey(budget?.statement, 'ingresos') || nodeByKey(budget?.margins_statement, 'ventas-productos'),
    [budget]
  );
  const grossNode = useMemo(() => nodeByKey(budget?.margins_statement, 'margen-bruto'), [budget]);
  const grossPct = useMemo(() => nodeByKey(budget?.margins_statement, 'margen-bruto-pct'), [budget]);
  const ebitdaNode = useMemo(() => nodeByKey(budget?.margins_statement, 'margen-ebitda'), [budget]);
  const ebitdaPct = useMemo(() => nodeByKey(budget?.margins_statement, 'margen-ebitda-pct'), [budget]);
  const monthlyBudget = (resultNode?.months || budget?.monthly_budget || []).slice(0, monthCount);
  const monthlyActual = (resultNode?.actual_months || budget?.monthly_actual || []).slice(0, monthCount);
  const resultadoBudget = resultNode?.budget_total ?? budget?.resultado_budget;
  const ingresosBudget = incomeNode?.budget_total ?? budget?.ingreso_budget;
  const gallonsSold = useMemo(() => totalSalesGallons(budget?.sales_items), [budget]);
  const grossPctLabel = formatPercent(grossPct?.budget_total);
  const ebitdaPctLabel = formatPercent(ebitdaPct?.budget_total);
  const periodCaption =
    monthAxis.length > 1
      ? `${monthAxis[0].label} – ${monthAxis[monthAxis.length - 1].label}`
      : monthAxis[0]?.label || periodTitle || '';
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <MetricCard
          toneKey="dark"
          isDarkMode={isDarkMode}
          icon={Wallet}
          label="Resultado presupuesto"
          value={loading ? '…' : formatCompact(resultadoBudget)}
          hint={`Ingresos ${money(budget?.ingreso_budget)} · Gastos ${money(budget?.gasto_budget)}`}
        />
        <MetricCard
          toneKey="mint"
          isDarkMode={isDarkMode}
          icon={TrendingUp}
          label="Total ingresos"
          value={loading ? '…' : formatCompact(ingresosBudget)}
          hint={loading ? '…' : `${formatGallons(gallonsSold)} vendidos`}
        />
        <MetricCard
          toneKey="sky"
          isDarkMode={isDarkMode}
          icon={Percent}
          label="Margen bruto"
          value={loading ? '…' : formatCompact(grossNode?.budget_total)}
          hint={grossPctLabel ? `${grossPctLabel} de las ventas` : 'Ventas − costo de producto − variables'}
        />
        <MetricCard
          toneKey="rose"
          isDarkMode={isDarkMode}
          icon={Activity}
          label="Margen EBITDA"
          value={loading ? '…' : formatCompact(ebitdaNode?.budget_total)}
          hint={ebitdaPctLabel ? `${ebitdaPctLabel} de las ventas` : 'Después de costos fijos y gastos'}
        />
      </div>
      <div
        style={{
          background: ui.panel,
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '10px 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <PillTabBar
            ariaLabel="Vista del resumen"
            items={OVERVIEW_TABS}
            activeKey={overviewTab}
            onChange={setOverviewTab}
          />
          {periodCaption ? (
            <span style={{ fontSize: 11, color: ui.muted }}>Periodo proyectado: {periodCaption}</span>
          ) : null}
        </div>
        <div style={{ padding: 12, display: 'grid', gap: 12 }}>
          {overviewTab === 'sales' ? (
            <BudgetSalesOverview
              budget={budget}
              monthAxis={monthAxis}
              ui={ui}
              isDarkMode={isDarkMode}
              loading={loading}
            />
          ) : overviewTab === 'cashflow' ? (
            <BudgetCashFlowOverview budget={budget} monthAxis={monthAxis} ui={ui} loading={loading} />
          ) : overviewTab === 'materials' ? (
            <BudgetRawMaterialsOverview budget={budget} monthAxis={monthAxis} ui={ui} loading={loading} />
          ) : (
            <>
              <BudgetVsActualChart
                monthlyBudget={monthlyBudget}
                monthlyActual={monthlyActual}
                monthAxis={monthAxis}
                periodTitle={periodTitle}
                ui={ui}
                isDarkMode={isDarkMode}
                loading={loading}
              />
              <BudgetStatementTable
                statement={budget?.statement || []}
                ui={ui}
                throughMonth={budget?.through_month || 12}
                monthCount={monthCount}
                monthAxis={monthAxis}
                budget={budget}
                canEdit={canEdit}
                onChanged={onChanged}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetOverview;
